import { z } from 'zod';

import { idSchema, isoDateTimeSchema, score10Schema, ulidSchema } from '../common';
import {
  agentStatusSchema,
  auditActorTypeSchema,
  browserStatusSchema,
  commentJobStatusSchema,
  commentRejectionReasonSchema,
  dedupMatchReasonSchema,
  platformSchema,
  platformSessionStatusSchema,
  projectStatusSchema,
  searchCycleStatusSchema,
  selectorHealthOutcomeSchema,
} from '../enums';
import { selectorHealthRuleKeySchema } from '../rest/selector-packs';
import { defineEventSchema, eventUnion } from './envelope';

/**
 * Server → Dashboard realtime events.
 *
 * These are *notifications*, not a second read API. Each one carries the
 * identifiers and the new state, so the dashboard can patch a row it already has
 * without a refetch — and nothing more. Anything richer (full post bodies, score
 * breakdowns, command history) stays behind the REST endpoints, where
 * authorisation is checked per request.
 *
 * The socket is authenticated and scoped to one workspace when it connects, so
 * these payloads do not repeat a workspace id except where the event is itself
 * workspace-wide.
 *
 * The dashboard shows agent-online, platform-logged-in and project-running as
 * three separate signals, and the first three events below are exactly those
 * three signals — kept separate here because they genuinely change
 * independently: an online agent can have a logged-out browser, and a
 * logged-in browser can have a paused project.
 */

// ---------------------------------------------------------------------------
// agent.status_changed
// ---------------------------------------------------------------------------

export const agentStatusChangedSchema = defineEventSchema('agent.status_changed', {
  agentId: idSchema,
  status: agentStatusSchema,
  previousStatus: agentStatusSchema.nullable(),
  statusMessage: z.string().max(1_000).nullable(),
  lastHeartbeatAt: isoDateTimeSchema.nullable(),
  occurredAt: isoDateTimeSchema,
});
export type AgentStatusChanged = z.infer<typeof agentStatusChangedSchema>;

// ---------------------------------------------------------------------------
// browser.status_changed
// ---------------------------------------------------------------------------

export const browserStatusChangedSchema = defineEventSchema('browser.status_changed', {
  agentId: idSchema,
  browserProfileId: idSchema,
  status: browserStatusSchema,
  previousStatus: browserStatusSchema.nullable(),
  /** True while a headed window is open for the user; drives the
   *  open_interactive / close_interactive button pair. */
  interactiveOpen: z.boolean(),
  detail: z.string().max(1_000).nullable(),
  occurredAt: isoDateTimeSchema,
});
export type BrowserStatusChanged = z.infer<typeof browserStatusChangedSchema>;

// ---------------------------------------------------------------------------
// platform.session_changed
// ---------------------------------------------------------------------------

/**
 * Display identity only — the same rule as the agent message this is derived
 * from. Name and avatar are what the agent read off the rendered page; no
 * cookie, token or profile path exists anywhere in this event.
 *
 * `platform` is the browser profile's channel, so the dashboard can word the
 * warning toast correctly ("Phiên Threads cần bạn can thiệp") without a lookup.
 *
 * `requiresUserAction` is precomputed from `SESSION_STATUSES_REQUIRING_USER` so
 * every surface agrees on when a human is needed. The system never tries to get
 * past a checkpoint, a 2FA prompt or a CAPTCHA — it stops and shows this flag.
 */
export const platformSessionChangedSchema = defineEventSchema('platform.session_changed', {
  agentId: idSchema,
  browserProfileId: idSchema,
  platform: platformSchema,
  sessionStatus: platformSessionStatusSchema,
  displayName: z.string().max(200).nullable(),
  avatarUrl: z.string().url().max(2_048).nullable(),
  requiresUserAction: z.boolean(),
  occurredAt: isoDateTimeSchema,
});
export type PlatformSessionChanged = z.infer<typeof platformSessionChangedSchema>;

// ---------------------------------------------------------------------------
// project.status_changed
// ---------------------------------------------------------------------------

export const projectStatusChangedSchema = defineEventSchema('project.status_changed', {
  projectId: idSchema,
  status: projectStatusSchema,
  previousStatus: projectStatusSchema.nullable(),
  statusMessage: z.string().max(1_000).nullable(),
  /** Straight from `PROJECT_TRANSITIONS`, so the UI can enable and disable the
   *  lifecycle buttons without duplicating the state machine. */
  allowedTransitions: z.array(projectStatusSchema),
  occurredAt: isoDateTimeSchema,
});
export type ProjectStatusChanged = z.infer<typeof projectStatusChangedSchema>;

// ---------------------------------------------------------------------------
// search.cycle_progress
// ---------------------------------------------------------------------------

/**
 * Progress of a running cycle. Emitted on cycle start, on every batch ack and on
 * completion — coarse enough that a long cycle does not flood the socket, fine
 * enough that the operator can see it moving.
 *
 * It is also emitted the instant a cycle is DISPATCHED, before the agent has
 * answered. That frame is what makes "Chạy ngay" change the screen under the
 * operator's finger: the agent has to launch Chromium and check the session
 * before it can report a cycle start, several seconds during which the line
 * otherwise went on displaying the PREVIOUS cycle's verdict — so pressing the
 * button appeared to do nothing, and the number next to it belonged to a run
 * that had already finished.
 */
export const searchCycleProgressSchema = defineEventSchema('search.cycle_progress', {
  /**
   * Null in exactly one case: the dispatch frame above, sent before the agent
   * has minted the cycle's id.
   *
   * Deliberately not faked with a server-side placeholder. The id identifies a
   * `search_cycles` row, and inventing one here would put an id on the wire that
   * matches no row and would never match the one the agent goes on to create.
   * Null says the true thing — a cycle has been asked for and does not exist yet.
   */
  searchCycleId: idSchema.nullable(),
  projectId: idSchema,
  agentId: idSchema,
  status: searchCycleStatusSchema,
  keywordsTotal: z.number().int().min(0),
  keywordsCompleted: z.number().int().min(0),
  postsFound: z.number().int().min(0),
  /** Accepted into `post_matches`; the gap against `postsFound` is duplicates. */
  postsIngested: z.number().int().min(0),
  currentKeywordTerm: z.string().max(120).nullable(),
  startedAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SearchCycleProgress = z.infer<typeof searchCycleProgressSchema>;

// ---------------------------------------------------------------------------
// post.ingested
// ---------------------------------------------------------------------------

/**
 * A post was committed for a project.
 *
 * `finalScore10` is nullable and usually null at this moment: ingestion and
 * deduplication run synchronously in the API, while scoring is a queued job.
 * The dashboard therefore shows the row immediately and fills the score in when
 * the later `post.ingested` revision or a refetch supplies it — it must not
 * treat a null score as "score zero".
 */
export const postIngestedSchema = defineEventSchema('post.ingested', {
  projectId: idSchema,
  /** Global post row — one Facebook post, however many projects found it. */
  postId: idSchema,
  /** Project-scoped join; every dashboard action addresses this id. */
  postMatchId: idSchema,
  batchId: ulidSchema,
  /** False when an existing post was refreshed rather than newly matched. */
  isNew: z.boolean(),
  dedupMatchReason: dedupMatchReasonSchema,
  matchedKeywordIds: z.array(idSchema).max(200),
  finalScore10: score10Schema.nullable(),
  discoveredAt: isoDateTimeSchema,
});
export type PostIngested = z.infer<typeof postIngestedSchema>;

// ---------------------------------------------------------------------------
// comment.job_updated
// ---------------------------------------------------------------------------

export const commentJobUpdatedSchema = defineEventSchema('comment.job_updated', {
  commentJobId: idSchema,
  projectId: idSchema,
  postId: idSchema,
  status: commentJobStatusSchema,
  previousStatus: commentJobStatusSchema.nullable(),
  /** Set when the policy engine, a rate limiter or the agent's local guard
   *  refused. A rejection is a decision, so the UI must not offer a plain retry. */
  rejectionReason: commentRejectionReasonSchema.nullable(),
  errorMessage: z.string().max(2_000).nullable(),
  occurredAt: isoDateTimeSchema,
});
export type CommentJobUpdated = z.infer<typeof commentJobUpdatedSchema>;

// ---------------------------------------------------------------------------
// selector.health_updated
// ---------------------------------------------------------------------------

/**
 * A selector rule's health moved. This is the early-warning channel: it lets an
 * admin see a rule degrade to its fallback candidates and publish a fixed pack
 * before the last candidate dies and collection stops entirely.
 */
export const selectorHealthUpdatedSchema = defineEventSchema('selector.health_updated', {
  ruleKey: selectorHealthRuleKeySchema,
  outcome: selectorHealthOutcomeSchema,
  packId: idSchema.nullable(),
  packVersionNumber: z.number().int().min(0).nullable(),
  agentId: idSchema.nullable(),
  failureCount: z.number().int().min(0),
  /** failures / total over the reporting window, 0.0-1.0. */
  failureRate: z.number().min(0).max(1),
  occurredAt: isoDateTimeSchema,
});
export type SelectorHealthUpdated = z.infer<typeof selectorHealthUpdatedSchema>;

// ---------------------------------------------------------------------------
// emergency_stop.changed
// ---------------------------------------------------------------------------

/**
 * The workspace-wide kill switch flipped.
 *
 * Workspace-scoped rather than project-scoped, which is why it carries a
 * `workspaceId`. While `active` is true every project is held down and no
 * comment is executed, regardless of individual project state — so the dashboard
 * must render this as a banner, not as one more status pill.
 */
export const emergencyStopChangedSchema = defineEventSchema('emergency_stop.changed', {
  workspaceId: idSchema,
  active: z.boolean(),
  reason: z.string().max(1_000).nullable(),
  /** Who flipped it — a user, an agent guard, or the system itself. */
  actorType: auditActorTypeSchema,
  actorId: idSchema.nullable(),
  affectedProjectIds: z.array(idSchema).max(500),
  changedAt: isoDateTimeSchema,
});
export type EmergencyStopChanged = z.infer<typeof emergencyStopChangedSchema>;

// ---------------------------------------------------------------------------
// The union
// ---------------------------------------------------------------------------

/**
 * Everything the dashboard socket may deliver. The store parses each frame with
 * this union and ignores what does not match, so an event added on the server
 * cannot crash an older tab that is still open.
 */
export const dashboardEventSchema = eventUnion([
  agentStatusChangedSchema,
  browserStatusChangedSchema,
  platformSessionChangedSchema,
  projectStatusChangedSchema,
  searchCycleProgressSchema,
  postIngestedSchema,
  commentJobUpdatedSchema,
  selectorHealthUpdatedSchema,
  emergencyStopChangedSchema,
]);
export type DashboardEvent = z.infer<typeof dashboardEventSchema>;
export type DashboardEventName = DashboardEvent['event'];
